import { GridColDef, GridFooterContainer, GridPaginationModel, GridRowModel, GridSortModel } from '@mui/x-data-grid';
import { CustomPagination } from '../../components/CDGrid';
import { AdCreativeSource, AdTagPlacement, AdTagPlacementStatus, AdTagType } from '../../types/AdTag';
import ApiService from '../../ApiService';
import Utils from '../../components/Utils';
import { AlertColor, Box, CircularProgress, IconButton, Tooltip } from '@mui/material';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Delete, Edit, FileCopy, Refresh, Wifi, WifiOff } from '@mui/icons-material';
import EditAdTagPlacement, { EditAdTagPlacementRef } from './EditAdTagPlacement';
import { AxiosPageableResponse } from '../../types/AxiosPageableResponse';

import useWebSocket from '../../hooks/useWebSocket';
import { InfoMessage, InfoMessageProps } from '../../components/InfoMessage';
import Block from '../../components/Block';
import Column from '../../components/Column';
import Row from '../../components/Row';
import CDButton from '../../components/CDButton';
import Heading from '../../components/Heading';
import AccessGuard from '../../components/AccessGuard';
import { USER_GROUP_ADMIN } from '../../types/User';
import AgencySelector from '../../components/AgencySelector';
import { AdvertiserContext } from '../../AdvertiserContext';
import { Dealer } from '../../types/Dealer';
import AdvertiserAutocomplete from '../../components/AdvertiserAutocomplete';
import { DataGridPro } from '@mui/x-data-grid-pro';
import usePageTitle from '../../hooks/usePageTitle';
import { CSVLink } from 'react-csv';
import { MediaTypeName } from '../../types/MediaType';

function AdTagGridFooter(props: {
    editAdTagPlacementRef: React.RefObject<EditAdTagPlacementRef | null>;
    reloadGrid: () => void;
    showInfoMessage: (alertColor: AlertColor, message: string, onClose?: () => void) => void;
    wsState: number;
    toggleWebSocket: () => void;
    totalElements: number;
    numberOfElements: number;
}) {
    const isOpen = props.wsState === WebSocket.OPEN;
    const isConnecting = props.wsState === WebSocket.CONNECTING;
    const title = isOpen ? 'Click to disconnect from real-time updates' : 'Click to connect to real-time updates';
    const sx = {
        ...{ display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' },
        ...{ width: 30, height: 30, borderRadius: '50%', backgroundColor: isOpen ? 'green' : 'lightgray' },
    };
    const wifi = isOpen ? <Wifi sx={{ color: 'white' }} /> : <WifiOff sx={{ color: 'white' }} />;
    return (
        <GridFooterContainer>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, marginLeft: '10px' }}>
                {isConnecting ? (
                    <Box sx={sx} title="Connecting...">
                        <CircularProgress size={20} />
                    </Box>
                ) : (
                    <Box sx={sx} title={title} onClick={props.toggleWebSocket}>
                        {wifi}
                    </Box>
                )}
                <Box>
                    <IconButton onClick={props.reloadGrid} aria-label="refresh" title="Refresh Grid">
                        <Refresh />
                    </IconButton>
                </Box>
                <EditAdTagPlacement
                    ref={props.editAdTagPlacementRef}
                    reloadGrid={props.reloadGrid}
                    showInfoMessage={props.showInfoMessage}
                />
            </Box>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginRight: '10px' }}>
                <CustomPagination />
                <span>
                    Showing {props.numberOfElements} of {props.totalElements} records
                </span>
            </div>
        </GridFooterContainer>
    );
}

export default function AdTagPlacementPage() {
    // State to hold all placements for export and modal visibility
    const [allPlacementsForExport, setAllPlacementsForExport] = useState<AdTagPlacement[] | null>(null);
    const [exportLoading, setExportLoading] = useState(false);
    const [exportModalOpen, setExportModalOpen] = useState(false);
    // Fetch all placements for export and show modal
    const fetchAllPlacementsForExport = async () => {
        setExportLoading(true);
        try {
            const response: AxiosPageableResponse<AdTagPlacement> = await ApiService.getAdTagPlacements(
                advertiserContext.agencyId,
                advertiserContext.dealer?.id,
                0,
                10000, // large enough for all records
                sortModel,
                true
            );
            if (response.status === 200) setAllPlacementsForExport(response.data.content);
            else setAllPlacementsForExport([]);
            setExportModalOpen(true);
        } catch {
            setAllPlacementsForExport([]);
            setExportModalOpen(true);
        } finally {
            setExportLoading(false);
        }
    };
    // Prepare formatted CSV data for export
    function getAdTagCsvData(placements: AdTagPlacement[]) {
        return placements.map((placement) => ({
            Status: placement.status,
            Agency: placement.dealer?.agency?.name,
            AgencyID: placement.dealer?.agency?.id,
            Advertiser: placement.dealer?.dealerName,
            AdvertiserID: placement.dealer?.id,
            DSPAdvertiserID: placement.dealer?.dspAdvertiser,
            Campaign: placement.campaign?.campaignName,
            AdGroup: placement.campaignAdGroup?.name,
            MediaType: MediaTypeName[placement.mediaType as keyof typeof MediaTypeName],
            DSPPlatform: placement.dspPlatform,
            TagType: placement.tagType,
            CreativeSource: placement.adCreativeSource,
            SourceFolder: placement.adTagImportSource
                ? `${placement.adTagImportSource.s3Bucket}/${placement.adTagImportSource.s3IncomingPath}`
                : '',
            AdTagName: placement.adName,
            ProjectID: placement.projectId,
            AdTagURL: placement.adUrl,
            LatestCreativeFile: placement.filename,
            CreatedDate: Utils.formatDateTime(placement.createdDate),
            UpdatedDate: Utils.formatDateTime(placement.updatedDate),
        }));
    }

    // Helper to get today's date in yyyymmdd format
    function getCsvFilename() {
        const now = new Date();
        const yyyy = now.getFullYear();
        const mm = String(now.getMonth() + 1).padStart(2, '0');
        const dd = String(now.getDate()).padStart(2, '0');
        return `Cognition Ad Tags_${yyyy}${mm}${dd}.csv`;
    }
    const editAdTagPlacementRef = useRef<EditAdTagPlacementRef | null>(null);
    const [adTagPlacements, setAdTagPlacements] = useState<AdTagPlacement[]>([]);
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(20);
    const [totalElements, setTotalElements] = useState(0);
    const [numberOfElements, setNumberOfElements] = useState(0);
    const [sortModel, setSortModel] = useState<GridSortModel>([{ field: 'updatedDate', sort: 'desc' }]);
    const [infoMessage, setInfoMessage] = useState<InfoMessageProps>({
        message: null,
    });
    const [triggerReload, setTriggerReload] = useState(false);
    const [tooltipOpenRow, setTooltipOpenRow] = useState<number | null>(null);
    const [hoverTooltipOpenRow, setHoverTooltipOpenRow] = useState<number | null>(null);
    const { advertiserContext, setAdvertiserContext, setDealer } = useContext(AdvertiserContext);

    // build & render title
    usePageTitle({ segments: ['Ad Serving'] });

    const handleDealerSelect = useCallback(
        (dealer: Dealer | null) => {
            setPage(0);
            setDealer(dealer === null ? new Dealer() : dealer);
        },
        [setDealer]
    );

    function showInfoMessage(alertColor: AlertColor, message: string, onClose?: () => void) {
        setInfoMessage({
            message: message,
            severity: alertColor,
            onClose: () => {
                setInfoMessage({ message: null });
                if (onClose) onClose();
            },
        });
    }

    const reloadGridToPage0 = (bypassWaitIndicator = true) => {
        if (page !== 0) setPage(0);
        else reloadGrid(bypassWaitIndicator);
    };

    const reloadGrid = async (bypassWaitIndicator?: boolean) => {
        const response: AxiosPageableResponse<AdTagPlacement> = await ApiService.getAdTagPlacements(
            advertiserContext.agencyId,
            advertiserContext.dealer?.id,
            page,
            pageSize,
            sortModel,
            bypassWaitIndicator
        );
        if (response.status === 200) {
            setAdTagPlacements(response.data.content);
            setTotalElements(response.data.totalElements);
            setNumberOfElements(response.data.numberOfElements);
        }
    };

    useEffect(() => {
        reloadGrid();
    }, [advertiserContext.agencyId, advertiserContext.dealer, page, pageSize, sortModel]);

    const { stompClient, readyState: wsState } = useWebSocket({
        topic: '/topic/adTagPlacement/publish',
        triggerReload,
        onMessage: (message) => {
            reloadGridToPage0();
            if (!message) return;
            const adTagPlacements: AdTagPlacement[] = JSON.parse(message.body);
            console.log('Received adTagPlacement:', adTagPlacements);
            for (const adTagPlacement of adTagPlacements) {
                if (adTagPlacement.status === AdTagPlacementStatus.ERROR) {
                    showInfoMessage(
                        'error',
                        `Error processing ${adTagPlacement.adName}: ${adTagPlacement.processMessage}`
                    );
                    break; // Stop processing on first error
                }
            }
        },
    });

    const toggleWebSocket = () => {
        if (wsState === WebSocket.OPEN) stompClient?.deactivate();
        else setTriggerReload(!triggerReload);
    };

    const handleCopyToClipboard = (adUrl: string, rowIndex: number) => {
        if (adUrl) {
            navigator.clipboard.writeText(adUrl).then(() => {
                setTooltipOpenRow(rowIndex);
                setHoverTooltipOpenRow(null);
                setTimeout(() => {
                    setTooltipOpenRow(null);
                    setHoverTooltipOpenRow(null);
                }, 2000); // Show tooltip for 2 seconds
            });
        }
    };

    const handleEdit = (placement?: AdTagPlacement) => {
        if (editAdTagPlacementRef.current) {
            editAdTagPlacementRef.current.edit(advertiserContext.agencyId, placement);
        }
    };

    const handleDelete = async (placement: AdTagPlacement) => {
        if (!window.confirm('Are you sure you want to disconnect this ad tag placement?')) {
            return;
        }
        await ApiService.deleteAdTagPlacement(placement.id as number);
        reloadGrid();
    };

    const columns: GridColDef[] = [
        {
            field: 'status',
            headerName: 'Status',
            renderCell: (rowData) => {
                if (AdTagPlacementStatus.ERROR === rowData.value) {
                    return (
                        <Tooltip title={rowData.row.processMessage}>
                            <span style={{ color: 'red' }}>{rowData.value}</span>
                        </Tooltip>
                    );
                }
                return rowData.value;
            },
            flex: 1,
        },
        {
            field: 'dealer.agency.name',
            headerName: 'Agency',
            valueGetter: (_: any, row: any) => row.dealer?.agency?.name,
            minWidth: 150,
        },
        {
            field: 'dealer.agency.id',
            headerName: 'AgencyId',
            valueGetter: (_: any, row: any) => row.dealer?.agency?.id,
            minWidth: 120,
        },
        {
            field: 'dealer',
            headerName: 'Advertiser',
            valueGetter: (value: { dealerName: string }) => value?.dealerName,
            minWidth: 150,
            flex: 2,
        },
        {
            field: 'dealer.id',
            headerName: 'AdvertiserId',
            valueGetter: (_: any, row: any) => row.dealer?.id,
            minWidth: 120,
        },
        {
            field: 'dealer.dspAdvertiser',
            headerName: 'DSP Advertiser ID',
            valueGetter: (_: any, row: any) => row.dealer?.dspAdvertiser,
            minWidth: 150,
            flex: 2,
        },
        {
            field: 'campaign',
            headerName: 'Campaign',
            valueGetter: (value: { campaignName: string }) => value?.campaignName,
            minWidth: 150,
            flex: 2,
        },
        {
            field: 'campaignAdGroup',
            headerName: 'Ad Group',
            valueGetter: (value: { name: string }) => value?.name,
            minWidth: 150,
            flex: 2,
        },
        {
            field: 'mediaType',
            headerName: 'Media Type',
            valueGetter: (value: string) => MediaTypeName[value as keyof typeof MediaTypeName],
            minWidth: 120,
            flex: 1,
        },
        {
            field: 'dspPlatform',
            headerName: 'DSP Platform',
            valueGetter: (value: string) => value,
            minWidth: 100,
            flex: 1,
        },
        {
            field: 'tagType',
            headerName: 'Tag Type',
            valueGetter: (value: string) => AdTagType[value as keyof typeof AdTagType],
            minWidth: 120,
            flex: 1,
        },
        {
            field: 'adCreativeSource',
            headerName: 'Creative Source',
            valueGetter: (value: string) => AdCreativeSource[value as keyof typeof AdCreativeSource],
            minWidth: 150,
            flex: 3,
        },
        {
            field: 'adTagImportSource',
            headerName: 'Source Folder',
            valueGetter: (value: { s3Bucket: string; s3IncomingPath: string }) =>
                value ? `${value.s3Bucket}/${value.s3IncomingPath}` : '',
            minWidth: 150,
            flex: 3,
        },

        { field: 'adName', headerName: 'Ad Tag Name', minWidth: 200, flex: 3 },
        { field: 'projectId', headerName: 'Last Project ID', minWidth: 100, flex: 2 },
        { field: 'adUrl', headerName: 'Ad Tag URL', minWidth: 150, flex: 3 },
        { field: 'filename', headerName: 'Latest Creative File', minWidth: 150, flex: 2 },
        {
            field: 'createdDate',
            headerName: 'Created Date',
            valueGetter: (value: Date) => Utils.formatDateTime(value),
            minWidth: 150,
            flex: 2,
        },
        {
            field: 'updatedDate',
            headerName: 'Updated Date',
            valueGetter: (value: Date) => Utils.formatDateTime(value),
            minWidth: 150,
            flex: 2,
        },
        {
            headerName: 'Actions',
            field: 'actions',
            sortable: false,
            renderCell: (rowData: any) => (
                <>
                    <Tooltip
                        title="Ad Tag Copied!"
                        open={tooltipOpenRow === rowData.id}
                        disableFocusListener
                        disableHoverListener
                        disableTouchListener
                        onClose={() => setTooltipOpenRow(null)}
                        placement="left"
                    >
                        <Tooltip
                            title={
                                rowData.row.adUrl
                                    ? 'Copy Ad Tag URL/Code'
                                    : 'Ad Tag URL not available. Please re-generate.'
                            }
                            open={hoverTooltipOpenRow === rowData.id && tooltipOpenRow !== rowData.id}
                            onOpen={() => setHoverTooltipOpenRow(rowData.id)}
                            onClose={() => setHoverTooltipOpenRow(null)}
                            placement="left"
                        >
                            <IconButton
                                onClick={() =>
                                    handleCopyToClipboard(rowData.row.adCode || rowData.row.adUrl, rowData.id)
                                }
                                sx={{ opacity: rowData.row.adCode || rowData.row.adUrl ? 1 : 0.5 }}
                            >
                                <FileCopy sx={{ width: 20, height: 20 }} />
                            </IconButton>
                        </Tooltip>
                    </Tooltip>
                    <IconButton onClick={() => handleEdit(rowData.row)} aria-label="edit" title="Edit">
                        <Edit sx={{ width: 20, height: 20 }} />
                    </IconButton>
                    <IconButton onClick={() => handleDelete(rowData.row)} aria-label="delete" title="Disconnect">
                        <Delete />
                    </IconButton>
                </>
            ),
            minWidth: 150,
        },
    ];

    return (
        <Column gap={0} flex={1}>
            <InfoMessage {...infoMessage} />

            <Block>
                <Column gap={1}>
                    <Row spacing={2} justifyContent="space-between" alignItems="center">
                        <Heading>Ad Serving</Heading>

                        <Row justifyContent="flex-end" gap={2}>
                            <CDButton
                                variant="outlined"
                                disabled={exportLoading || adTagPlacements.length === 0}
                                onClick={fetchAllPlacementsForExport}
                            >
                                {exportLoading ? (
                                    <>
                                        <CircularProgress size={18} sx={{ mr: 1 }} /> Preparing Export...
                                    </>
                                ) : (
                                    'Export CSV'
                                )}
                            </CDButton>
                            <CDButton onClick={() => handleEdit()} variant="contained">
                                Setup Ad Tag
                            </CDButton>
                        </Row>
                    </Row>
                </Column>
            </Block>

            {/* Export Modal */}
            {exportModalOpen && (
                <Box
                    sx={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        width: '100vw',
                        height: '100vh',
                        background: 'rgba(0,0,0,0.3)',
                        zIndex: 9999,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                    onClick={() => setExportModalOpen(false)}
                >
                    <Box
                        sx={{
                            background: 'white',
                            borderRadius: 2,
                            boxShadow: 3,
                            p: 4,
                            minWidth: 350,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <Heading>Download Ad Tag CSV Report</Heading>
                        <Box sx={{ mt: 2 }}>
                            <CSVLink
                                data={getAdTagCsvData(allPlacementsForExport || [])}
                                filename={getCsvFilename()}
                                style={{ textDecoration: 'none' }}
                                target="_blank"
                                onClick={() => setExportModalOpen(false)}
                            >
                                <CDButton
                                    variant="text"
                                    disabled={!allPlacementsForExport || allPlacementsForExport.length === 0}
                                >
                                    Download Ad Tag CSV Report
                                </CDButton>
                            </CSVLink>
                        </Box>
                        <CDButton sx={{ mt: 2 }} variant="contained" onClick={() => setExportModalOpen(false)}>
                            Close
                        </CDButton>
                    </Box>
                </Box>
            )}

            <Block>
                <Row>
                    <AccessGuard accessGroup={USER_GROUP_ADMIN}>
                        <div style={{ minWidth: '280px' }}>
                            <AgencySelector
                                variant="outlined"
                                label="Agency"
                                agencySelected={(agencyId) => {
                                    setPage(0);
                                    setAdvertiserContext(null, agencyId);
                                }}
                                agencyId={advertiserContext.agencyId}
                            />
                        </div>
                    </AccessGuard>

                    <div style={{ minWidth: '500px' }}>
                        <AdvertiserAutocomplete
                            label="Advertiser"
                            size="small"
                            value={advertiserContext.dealer?.id ?? null}
                            criteria={{
                                agencyIds: advertiserContext.agencyId ? [advertiserContext.agencyId] : [],
                            }}
                            onChange={handleDealerSelect}
                            showInactiveAdvertisersToggle={true}
                        />
                    </div>
                </Row>
            </Block>

            <Block style={{ width: '100%', overflow: 'auto', height: 'calc(100vh - 280px)' }}>
                <DataGridPro
                    columns={columns}
                    rows={adTagPlacements}
                    processRowUpdate={(row: GridRowModel) => {
                        // ApiService.updateAdServerPlacement(row as CampaignAdServerPlacement);
                        return row;
                    }}
                    onProcessRowUpdateError={() => {}}
                    rowCount={totalElements}
                    initialState={{
                        pagination: { paginationModel: { page: page, pageSize: pageSize } },

                        columns: {
                            columnVisibilityModel: {
                                'dealer.agency.id': false,
                                'dealer.id': false,
                                'dealer.dspAdvertiser': false,
                                campaign: false,
                                campaignAdGroup: false,
                                mediaType: false,
                                adCreativeSource: false,
                                adTagImportSource: false,
                                projectId: false,
                                adUrl: false,
                            },
                        },
                        pinnedColumns: {
                            right: ['actions'],
                        },
                    }}
                    onPaginationModelChange={(model: GridPaginationModel) => {
                        setPage(model.page);
                        setPageSize(model.pageSize);
                    }}
                    onSortModelChange={setSortModel}
                    paginationMode="server"
                    sortingMode="server"
                    sortModel={sortModel}
                    slots={{
                        footer: () => (
                            <AdTagGridFooter
                                editAdTagPlacementRef={editAdTagPlacementRef}
                                reloadGrid={reloadGridToPage0}
                                showInfoMessage={showInfoMessage}
                                toggleWebSocket={toggleWebSocket}
                                wsState={wsState}
                                // pageSize={pageSize}
                                totalElements={totalElements}
                                numberOfElements={numberOfElements}
                            />
                        ),
                    }}
                    sx={{ flexGrow: 1 }}
                    disableColumnPinning
                />
            </Block>
        </Column>
    );
}
